// services/commissionStorageService.js
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { calcCommission, generateTeamReport } from '../utils/commissionCalculator';

/**
 * Clean an object by removing undefined values recursively
 */
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => cleanObject(item)).filter(item => item !== null && item !== undefined);
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    const cleanedValue = cleanObject(value);
    if (cleanedValue !== null && cleanedValue !== undefined) {
      cleaned[key] = cleanedValue;
    }
  }
  return cleaned;
};

/**
 * Store commission record for an agent in Firestore
 */
export const storeCommissionRecord = async (commissionData) => {
  try {
    const { agentId, year, month } = commissionData;
    const period = `${year}-${String(month).padStart(2, '0')}`;
    
    const documentId = `${agentId}_${period}`;
    const commissionRef = doc(db, 'commissions', documentId);
    
    const data = {
      ...commissionData,
      period,
      calculated_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const cleanedData = cleanObject(data);
    
    await setDoc(commissionRef, cleanedData, { merge: true });
    
    console.log(`Commission record stored for agent ${agentId} for period ${period}`);
  } catch (error) {
    console.error('Error storing commission record:', error);
    throw error;
  }
};

/**
 * Calculate and store commission for a single agent (individual sales only)
 */
export const calculateAndStoreAgentCommission = async (agentSalesData) => {
  try {
    const { 
      agentId, 
      agentName, 
      agentRole, 
      totalSales,
      totalSalesUSD,
      orderCount, 
      orders, 
      year, 
      month,
      currencyBreakdown,
      teamLeader,
      isTeamLeader: agentIsTeamLeader
    } = agentSalesData;
    
    const salesForCommission = totalSalesUSD || 0;
    
    // Use the isTeamLeader flag from the data service
    const isTeamLeader = agentIsTeamLeader || false;
    const hasTeamLeader = !!teamLeader;
    
    let individualCommission = 0;
    let commissionType = 'TM';
    let overrideGenerated = 0;
    
    if (isTeamLeader) {
      individualCommission = calcCommission(salesForCommission, 'TL');
      commissionType = 'TL';
      overrideGenerated = 0;
    } else if (hasTeamLeader) {
      individualCommission = calcCommission(salesForCommission, 'TM');
      commissionType = 'TM';
      overrideGenerated = calcCommission(salesForCommission, 'OV');
    } else {
      individualCommission = calcCommission(salesForCommission, 'TM');
      commissionType = 'TM';
      overrideGenerated = 0;
    }
    
    const formattedOrders = (orders || []).map(order => ({
      orderId: order.id || order.orderId || '',
      orderNumber: order.code || order.orderNumber || order.orderDocRef || '',
      amount: order.amount || 0,
      amountUSD: order.amountUSD || 0,
      currency: order.currency || 'USD',
      date: order.date || order.orderDate || new Date().toISOString(),
      country: order.country || '',
      customer: {
        name: order.customer?.name || '',
        mobile: order.customer?.mobile || order.customer?.mobile_number || '',
        email: order.customer?.email || '',
        billing_address: order.customer?.billing_address || '',
        shipping_address: order.customer?.shipping_address || ''
      }
    }));
    
    const commissionRecord = {
      agentId,
      agentName: agentName || '',
      agentRole: agentRole || 'TM',
      year,
      month,
      
      totalSales: totalSales || 0,
      totalSalesUSD: totalSalesUSD || 0,
      orderCount: orderCount || 0,
      currencyBreakdown: currencyBreakdown || {},
      orders: formattedOrders,
      
      individualCommission: parseFloat(individualCommission.toFixed(2)),
      commissionType: commissionType,
      overrideGenerated: parseFloat(overrideGenerated.toFixed(2)),
      
      isTeamLeader: isTeamLeader,
      hasTeamLeader: hasTeamLeader,
      teamLeaderId: teamLeader || null,
      
      hasTeam: false,
      overridePaid: 0,
      totalCommission: parseFloat(individualCommission.toFixed(2)),
      teamMembers: [],
      totalTeamSales: 0,
      totalTeamSalesUSD: 0,
      totalTeamOverrideGenerated: 0
    };
    
    await storeCommissionRecord(commissionRecord);
    
    return commissionRecord;
  } catch (error) {
    console.error('Error calculating and storing agent commission:', error);
    throw error;
  }
};

/**
 * Calculate override for a Team Leader based on team members' sales
 */
export const calculateTeamLeaderOverride = async (teamLeaderData, teamMembersData, tlTarget = 1000) => {
  try {
    const { agentId, year, month } = teamLeaderData;
    const period = `${year}-${String(month).padStart(2, '0')}`;
    const documentId = `${agentId}_${period}`;
    const tlCommissionRef = doc(db, 'commissions', documentId);
    
    const docSnap = await getDoc(tlCommissionRef);
    if (!docSnap.exists()) {
      console.warn(`No commission record found for TL ${agentId}, skipping override`);
      return null;
    }
    
    const existingRecord = docSnap.data();
    
    const teamTotalSalesUSD = teamMembersData.reduce((sum, tm) => sum + (tm.totalSalesUSD || 0), 0);
    const teamTotalSales = teamMembersData.reduce((sum, tm) => sum + (tm.totalSales || 0), 0);
    
    let totalOverrideGenerated = 0;
    teamMembersData.forEach(tm => {
      totalOverrideGenerated += (tm.overrideGenerated || 0);
    });
    
    const tlSalesUSD = teamLeaderData.totalSalesUSD || 0;
    const targetRatio = tlTarget > 0 ? Math.min(tlSalesUSD / tlTarget, 1) : 0;
    const overridePaidToTL = totalOverrideGenerated * targetRatio;
    
    const teamMembersForReport = teamMembersData.map(tm => ({
      agentId: tm.agentId,
      agentName: tm.agentName,
      sales: tm.totalSalesUSD || 0
    }));
    
    const teamReport = generateTeamReport(
      teamMembersForReport,
      tlSalesUSD,
      tlTarget
    );
    
    const tlUpdateData = {
      ...existingRecord,
      overridePaid: parseFloat(overridePaidToTL.toFixed(2)),
      totalCommission: parseFloat((existingRecord.totalCommission + overridePaidToTL).toFixed(2)),
      hasTeam: true,
      teamMembers: teamMembersData.map(tm => ({
        agent_id: tm.agentId,
        agent_name: tm.agentName || '',
        sales: tm.totalSales || 0,
        sales_usd: tm.totalSalesUSD || 0,
        order_count: tm.orderCount || 0,
        individual_commission: tm.individualCommission || 0,
        commission_type: tm.commissionType || 'TM',
        override_generated: tm.overrideGenerated || 0,
        orders: (tm.orders || []).map(order => ({
          orderId: order.orderId || order.id || '',
          orderNumber: order.orderNumber || order.code || order.orderDocRef || '',
          amount: order.amount || 0,
          amountUSD: order.amountUSD || 0,
          currency: order.currency || 'USD',
          date: order.date || order.orderDate || '',
          country: order.country || '',
          customer: {
            name: order.customer?.name || '',
            mobile: order.customer?.mobile || order.customer?.mobile_number || '',
            email: order.customer?.email || ''
          }
        }))
      })),
      totalTeamSales: parseFloat(teamTotalSales.toFixed(2)),
      totalTeamSalesUSD: parseFloat(teamTotalSalesUSD.toFixed(2)),
      totalTeamOverrideGenerated: parseFloat(totalOverrideGenerated.toFixed(2)),
      tl_target: tlTarget,
      target_attainment: targetRatio,
      target_met: targetRatio >= 1,
      override_breakdown: {
        total_override_generated: parseFloat(teamReport.overrideBreakdown.totalOverrideGenerated.toFixed(2)),
        override_paid_to_tl: parseFloat(teamReport.overrideBreakdown.overridePaidToTL.toFixed(2)),
        override_withheld: parseFloat(teamReport.overrideBreakdown.overrideWithheld.toFixed(2)),
        withholding_reason: teamReport.overrideBreakdown.withholdingReason || ''
      }
    };
    
    await setDoc(tlCommissionRef, tlUpdateData, { merge: true });
    
    console.log(`  ✓ TL ${agentId} override calculated: ${overridePaidToTL.toFixed(2)} (USD) from ${teamMembersData.length} team members`);
    
    return tlUpdateData;
  } catch (error) {
    console.error('Error calculating team leader override:', error);
    throw error;
  }
};

/**
 * Calculate and store commissions for all agents for a month
 */
export const calculateAndStoreAllCommissions = async (year, month, tlTarget = 1000) => {
  try {
    const { calculateAllAgentsMonthlySales, groupAgentsByTeamLeader } = await import('./commissionDataService');
    
    const allAgentsSales = await calculateAllAgentsMonthlySales(year, month);
    
    console.log(`\n=== PROCESSING ${allAgentsSales.length} AGENTS FOR ${year}-${month} ===`);
    
    console.log('\n--- PASS 1: Calculating individual commissions for all agents ---');
    
    const allAgentRecords = [];
    const teamLeaderMap = new Map();
    
    for (const agent of allAgentsSales) {
      const isTeamLeader = agent.isTeamLeader || false;
      const hasTeamLeader = !!agent.teamLeader;
      
      const record = await calculateAndStoreAgentCommission(agent);
      allAgentRecords.push(record);
      
      if (isTeamLeader) {
        console.log(`  ✓ TL ${agent.agentId} (${agent.agentName}) - TL commission: ${record.individualCommission} (USD) [from own sales]`);
        teamLeaderMap.set(agent.agentId, {
          agentData: agent,
          record: record
        });
      } else if (hasTeamLeader) {
        console.log(`  ✓ TM ${agent.agentId} (${agent.agentName}) - TM commission: ${record.individualCommission} + OV generated: ${record.overrideGenerated} (USD) [TL: ${agent.teamLeader}]`);
      } else {
        console.log(`  ✓ Agent ${agent.agentId} (${agent.agentName}) - TM commission: ${record.individualCommission} (USD) [no team]`);
      }
    }
    
    console.log('\n--- PASS 2: Calculating override for Team Leaders ---');
    
    const teams = groupAgentsByTeamLeader(allAgentsSales);
    
    const overrideResults = [];
    let teamsProcessed = 0;
    let teamsSkipped = 0;
    
    for (const [tlId, teamMembers] of Object.entries(teams)) {
      const tlData = teamLeaderMap.get(tlId);
      
      if (!tlData) {
        console.warn(`  ⚠ TL ${tlId} not found in team leader map, skipping`);
        teamsSkipped++;
        continue;
      }
      
      if (teamMembers.length === 0) {
        console.log(`  ℹ TL ${tlId} has no team members, skipping override`);
        teamsSkipped++;
        continue;
      }
      
      console.log(`  Processing TL ${tlId} with ${teamMembers.length} team members`);
      
      const overrideResult = await calculateTeamLeaderOverride(
        tlData.agentData,
        teamMembers,
        tlTarget
      );
      
      if (overrideResult) {
        overrideResults.push(overrideResult);
        teamsProcessed++;
      } else {
        teamsSkipped++;
      }
    }
    
    console.log('\n--- SUMMARY ---');
    console.log(`Total agents processed: ${allAgentRecords.length}`);
    console.log(`  - Team Leaders (role='admin' or 'teamLeader'): ${teamLeaderMap.size}`);
    console.log(`  - Team Members (have teamLeader field): ${allAgentRecords.filter(r => r.hasTeamLeader && !r.isTeamLeader).length}`);
    console.log(`  - Independent Agents: ${allAgentRecords.filter(r => !r.hasTeamLeader && !r.isTeamLeader).length}`);
    console.log(`Teams processed with override: ${teamsProcessed}`);
    console.log(`Teams skipped (no members or error): ${teamsSkipped}`);
    console.log(`Total override distributed: ${overrideResults.reduce((sum, r) => sum + (r.overridePaid || 0), 0).toFixed(2)} (USD)`);
    
    return {
      year,
      month,
      totalAgentsProcessed: allAgentsSales.length,
      totalTeamLeaders: teamLeaderMap.size,
      teamsProcessed: teamsProcessed,
      teamsSkipped: teamsSkipped,
      allAgentRecords,
      overrideResults,
      tlTarget
    };
  } catch (error) {
    console.error('Error calculating and storing all commissions:', error);
    throw error;
  }
};

/**
 * Get commission record for an agent for a specific period
 */
export const getAgentCommission = async (agentId, year, month) => {
  try {
    const period = `${year}-${String(month).padStart(2, '0')}`;
    const documentId = `${agentId}_${period}`;
    const commissionRef = doc(db, 'commissions', documentId);
    const docSnap = await getDoc(commissionRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching agent commission:', error);
    throw error;
  }
};

/**
 * Get all commission records for an agent
 */
export const getAgentCommissionHistory = async (agentId) => {
  try {
    const commissionsRef = collection(db, 'commissions');
    const querySnapshot = await getDocs(commissionsRef);
    
    const commissions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.agentId === agentId) {
        commissions.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    commissions.sort((a, b) => {
      if (a.period > b.period) return -1;
      if (a.period < b.period) return 1;
      return 0;
    });
    
    return commissions;
  } catch (error) {
    console.error('Error fetching agent commission history:', error);
    throw error;
  }
};
